/**
 * The bulk bar. Appears on selection, disappears when the selection is cleared.
 *
 * Two actions, both from spec 02-orders: Reprint and Change status. There is deliberately
 * no bulk mark paid, cancel or refund; the API refuses those outright and the bar does not
 * pretend otherwise.
 *
 * Reprint is a fan-out, one request per order, each with its own Idempotency-Key. That is
 * not a shortcut around a missing bulk route: the bridge memoises a reprint per order under
 * its command id, so one key covering fifty orders would make the retry of a partial
 * failure a no-op for every order that had already printed. One key per order is what makes
 * a retry safe, and what makes the per-order result below honest.
 */
import { useState } from 'react';
import type { BulkReprintOutcome, PrintDoc } from '@elutide/shared';
import { Button } from '@/components';
import { Menu, MenuItem } from './Menu';

export type BulkStatusAction = 'mark_packed' | 'handed_to_ups';

export interface OrdersBulkBarProps {
  selected: readonly number[];
  onClear: () => void;
  onReprint: (docs: readonly PrintDoc[]) => void;
  /** Absent for a role without `orders.bulk`; the Change status menu is not offered. */
  onStatus?: ((action: BulkStatusAction) => void) | undefined;
  busy: boolean;
  /** One line per order from the last fan-out, or null when there has not been one. */
  results: readonly BulkReprintOutcome[] | null;
  onDismissResults: () => void;
}

const REPRINT_CHOICES: readonly { id: string; label: string; docs: PrintDoc[] }[] = [
  { id: 'slips', label: 'Packing slips', docs: ['packing_slip'] },
  { id: 'coas', label: 'Certificates', docs: ['coa'] },
  { id: 'labels', label: 'Shipping labels', docs: ['label'] },
  { id: 'all', label: 'All three', docs: ['packing_slip', 'coa', 'label'] },
];

const STATUS_CHOICES: readonly { id: BulkStatusAction; label: string; hint: string }[] = [
  { id: 'mark_packed', label: 'Mark packed', hint: 'Sets packed, no photo recorded' },
  { id: 'handed_to_ups', label: 'Mark shipped', hint: 'The manual route to shipped' },
];

export function OrdersBulkBar({
  selected,
  onClear,
  onReprint,
  onStatus,
  busy,
  results,
  onDismissResults,
}: OrdersBulkBarProps) {
  const [menu, setMenu] = useState<'reprint' | 'status' | null>(null);

  if (selected.length === 0 && results === null) return null;

  return (
    <div className="ord-bulk" role="region" aria-label="Bulk actions">
      {results ? (
        <BulkResults results={results} onDismiss={onDismissResults} />
      ) : (
        <div className="ord-bulk__row">
          <span className="ord-bulk__count" aria-live="polite">
            {selected.length} selected
          </span>

          <Menu
            label="Reprint"
            open={menu === 'reprint'}
            onToggle={() => setMenu((current) => (current === 'reprint' ? null : 'reprint'))}
            onClose={() => setMenu(null)}
            busy={busy}
          >
            {REPRINT_CHOICES.map((choice) => (
              <MenuItem
                key={choice.id}
                label={choice.label}
                onSelect={() => {
                  setMenu(null);
                  onReprint(choice.docs);
                }}
              />
            ))}
          </Menu>

          {onStatus ? (
            <Menu
              label="Change status"
              open={menu === 'status'}
              onToggle={() => setMenu((current) => (current === 'status' ? null : 'status'))}
              onClose={() => setMenu(null)}
              busy={busy}
            >
              {STATUS_CHOICES.map((choice) => (
                <MenuItem
                  key={choice.id}
                  label={choice.label}
                  hint={choice.hint}
                  onSelect={() => {
                    setMenu(null);
                    onStatus(choice.id);
                  }}
                />
              ))}
            </Menu>
          ) : null}

          <Button variant="ghost" size="sm" onClick={onClear}>
            Clear
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * What actually happened, order by order.
 *
 * A bulk action that reports "done" when three of fifty failed is worse than one that
 * reports nothing, so every order gets a line: printed, replayed (the bridge had already
 * seen this command and queued nothing a second time), or the reason it did not.
 */
function BulkResults({
  results,
  onDismiss,
}: {
  results: readonly BulkReprintOutcome[];
  onDismiss: () => void;
}) {
  const ok = results.filter((result) => result.ok).length;
  const replayed = results.filter((result) => result.replayed).length;

  return (
    <div className="ord-bulk__results">
      <div className="ord-bulk__row">
        <span className="ord-bulk__count" role="status">
          {ok} of {results.length} sent to the printer
          {replayed > 0 ? `, ${replayed} already queued earlier` : ''}
        </span>
        <Button variant="ghost" size="sm" onClick={onDismiss}>
          Dismiss
        </Button>
      </div>
      <ul className="ord-bulk__list" role="list">
        {results.map((result) => (
          <li key={result.order_id} data-ok={result.ok ? 'true' : 'false'}>
            <span className="ord-num">{result.order_id}</span>
            <span>
              {result.ok
                ? result.replayed
                  ? 'already queued'
                  : `sent ${result.accepted.join(', ')}`
                : (result.error ?? 'failed')}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
